import { useState, useEffect, useCallback } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";

import BlogMainPage from "./components/BlogMainPage.js";
import PostsPage from "./components/PostsPage.js";
import ProjectsPage from "./components/ProjectsPage.js";
import AboutPage from "./components/AboutPage.js";
import CurrentPostPage from "./components/CurrentPostPage.js";
import NotFound from "../../app/components/NotFound.js";

import MainMenu from "../../app/components/MainMenu.jsx";
import NewProjectPopup from "./components/NewProjectPopup.js";
import NewPostPopup from "./components/NewPostPopup.js";
import EditPostPopup from "./components/EditPostPopup.js";
import EditProjectPopup from "./components/EditProjectPopup.js";
import DeletePostModal from "./components/DeletePostModal.js";
import DeleteProjectModal from "./components/DeleteProjectModal.js";

import api from "../../shared/utils/api.js";

import useProjects from "./hooks/useProjects.js";
import usePosts from "./hooks/usePosts.js";

import getCurrentActivePage from "./utils/getCurrentActivePage.js";

function BlogRoot({
  loggedIn,
  currentUser,
  handleSignout,
  isLoading,
  openModal,
  startLoading,
  stopLoading,
  screenWidth,
  onContactClick,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isBlogMenuOpen, setIsBlogMenuOpen] = useState(false);
  const [isPostPopupOpen, setIsPostPopupOpen] = useState(false);
  const [isEditPostPopupOpen, setIsEditPostPopupOpen] = useState(false);
  const [isEditProjectPopupOpen, setIsEditProjectPopupOpen] = useState(false);
  const [isNewProjectPopupOpen, setIsNewProjectPopupOpen] = useState(false);
  const [isDeletePostModalOpen, setIsDeletePostModalOpen] = useState(false);
  const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] =
    useState(false);
  const isAlinaRoute = location.pathname.startsWith("/journal");
  const [projectHashtags, setProjectHashtags] = useState([]);
  const [activePostHashtag, setActivePostHashtag] = useState("All");
  const [activeProjectHashtag, setActiveProjectHashtag] = useState("All");
  const [redirectAfterDelete, setRedirectAfterDelete] = useState(false);
  const [query, setQuery] = useState("");
  const [activeBlogPage, setActiveBlogPage] = useState("Home");

  useEffect(() => {
    if (!isAlinaRoute || projectHashtags.length > 0) {
      return;
    }
    api
      .getProjectHashtags()
      .then((hashtags) => {
        setProjectHashtags(hashtags);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [isAlinaRoute, projectHashtags.length]);

  useEffect(() => {
    setQuery("");
    setActivePostHashtag("All");
    setActiveProjectHashtag("All");
  }, [location.pathname]);

  useEffect(() => {
    const currentPage = getCurrentActivePage(location.pathname);
    if (currentPage === "journal") {
      setActiveBlogPage("Home");
    } else {
      setActiveBlogPage(currentPage);
    }
  }, [location.pathname]);

  function handleNewPostPopupOpen() {
    setIsPostPopupOpen(true);
  }

  function handleNewProjectPopupOpen() {
    setIsNewProjectPopupOpen(true);
  }

  function handleBlogMenuClick() {
    setIsBlogMenuOpen(!isBlogMenuOpen);
  }

  function moveToHomePage() {
    navigate("/journal");
    closeBlogMenu();
  }
  function moveToPostsPage() {
    closeBlogMenu();
  }
  function moveToProjectsPage() {
    closeBlogMenu();
  }

  function moveToAboutPage() {
    closeBlogMenu();
  }

  function handlePostClick(post) {
    navigate(`/journal/posts/${post._id}`);
  }

  function moveToPreviousPage() {
    navigate(-1);
  }

  function viewAllPostsClick() {
    navigate("./posts");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  function viewAllProjectsClick() {
    navigate("./projects");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  const closeAllBlogPopups = useCallback(() => {
    setIsPostPopupOpen(false);
    setIsEditPostPopupOpen(false);
    setIsEditProjectPopupOpen(false);
    setIsNewProjectPopupOpen(false);
    setIsDeletePostModalOpen(false);
    setIsDeleteProjectModalOpen(false);
    setRedirectAfterDelete(false);
  }, []);

  const closeBlogMenu = useCallback(() => {
    setIsBlogMenuOpen(false);
  }, []);

  const {
    projectsToRender,
    totalProjects,
    currentProjectsNumber,
    hasMoreProjects,
    calculateProjectsCount,
    showMoreProjects,
    handleProjectHashtagClick,
    handleAddProject,
    handleEditProject,
    handleEditProjectPopupOpen,
    projectToEdit,
    handleProjectDelete,
    handleDeleteProjectModalOpen,
    projectToDelete,
    isProjectsLoading,
  } = useProjects({
    screenWidth,
    isAlinaRoute,
    openModal,
    activeProjectHashtag,
    setActiveProjectHashtag,
    startLoading,
    stopLoading,
    closeAllBlogPopups,
    setIsEditProjectPopupOpen,
    setIsDeleteProjectModalOpen,
  });

  const {
    postsToRender,
    totalPosts,
    currentPostsNumber,
    hasMorePosts,
    calculatePostsCount,
    showMorePosts,
    handlePostHashtagClick,
    handlePostsSearch,
    handleAddPost,
    handleEditPostPopupOpen,
    postToEdit,
    handleEditPost,
    handleDeletePostModalOpen,
    handlePostDelete,
    postToDelete,
    postVersion,
    isPostsLoading,
  } = usePosts({
    screenWidth,
    isAlinaRoute,
    query,
    setQuery,
    activePostHashtag,
    setActivePostHashtag,
    startLoading,
    stopLoading,
    openModal,
    closeAllBlogPopups,
    setIsEditPostPopupOpen,
    setIsDeletePostModalOpen,
    setRedirectAfterDelete,
    redirectAfterDelete,
  });

  const previewProjectsQuantity = screenWidth > 1200 ? 3 : 2;

  useEffect(() => {
    calculatePostsCount();
    calculateProjectsCount();
  }, [calculatePostsCount, calculateProjectsCount]);

  return (
    <>
      <Routes>
        <Route
          index
          element={
            <BlogMainPage
              loggedIn={loggedIn}
              currentUser={currentUser}
              onLogout={handleSignout}
              totalPosts={totalPosts}
              postsToRender={postsToRender}
              projectsToRender={projectsToRender}
              totalProjects={totalProjects}
              projectsQuantity={previewProjectsQuantity}
              isPostsLoading={isPostsLoading}
              isProjectsLoading={isProjectsLoading}
              onBlogMenuClick={handleBlogMenuClick}
              onContactClick={onContactClick}
              onNewPostClick={handleNewPostPopupOpen}
              onNewProjectClick={handleNewProjectPopupOpen}
              onViewAllPostsClick={viewAllPostsClick}
              onViewAllProjectsClick={viewAllProjectsClick}
              onPostClick={handlePostClick}
              onEditPostButtonClick={handleEditPostPopupOpen}
              onDeletePostButtonClick={handleDeletePostModalOpen}
              onEditProjectButtonClick={handleEditProjectPopupOpen}
              onDeleteProjectButtonClick={handleDeleteProjectModalOpen}
              onHomeClick={moveToHomePage}
              onPostsClick={moveToPostsPage}
              onProjectsClick={moveToProjectsPage}
              onAboutClick={moveToAboutPage}
              isMenuOpen={isBlogMenuOpen}
            />
          }
        />

        <Route
          path="posts"
          element={
            <PostsPage
              loggedIn={loggedIn}
              currentUser={currentUser}
              onLogout={handleSignout}
              activePage={activeBlogPage}
              postsToRender={postsToRender}
              onNewPostClick={handleNewPostPopupOpen}
              onEditPostButtonClick={handleEditPostPopupOpen}
              onDeletePostButtonClick={handleDeletePostModalOpen}
              onBlogMenuClick={handleBlogMenuClick}
              onContactClick={onContactClick}
              onPostsSearch={handlePostsSearch}
              onPostClick={handlePostClick}
              hasMorePosts={hasMorePosts}
              postsQuantity={currentPostsNumber}
              onShowMorePosts={showMorePosts}
              isLoading={isLoading}
              isPostsLoading={isPostsLoading}
              query={query}
              querySetter={setQuery}
              onPostHashtagClick={handlePostHashtagClick}
              activeHashtag={activePostHashtag}
              isMenuOpen={isBlogMenuOpen}
            />
          }
        />

        <Route
          path="posts/:id"
          element={
            <CurrentPostPage
              activePage="posts"
              loggedIn={loggedIn}
              currentUser={currentUser}
              onLogout={handleSignout}
              onBlogMenuClick={handleBlogMenuClick}
              onContactClick={onContactClick}
              onBackButtonClick={moveToPreviousPage}
              onEditPostButtonClick={handleEditPostPopupOpen}
              onDeletePostButtonClick={handleDeletePostModalOpen}
              postVersion={postVersion}
              openModal={openModal}
              isMenuOpen={isBlogMenuOpen}
            />
          }
        />

        <Route
          path="projects"
          element={
            <ProjectsPage
              loggedIn={loggedIn}
              currentUser={currentUser}
              onLogout={handleSignout}
              activePage={activeBlogPage}
              hashtags={projectHashtags}
              activeProjectHashtag={activeProjectHashtag}
              projectsToRender={projectsToRender}
              hasMoreProjects={hasMoreProjects}
              onNewProjectClick={handleNewProjectPopupOpen}
              onBlogMenuClick={handleBlogMenuClick}
              onContactClick={onContactClick}
              projectsQuantity={currentProjectsNumber}
              onShowMoreProjects={showMoreProjects}
              onEditProjectButtonClick={handleEditProjectPopupOpen}
              onDeleteProjectButtonClick={handleDeleteProjectModalOpen}
              onProjectHashtagClick={handleProjectHashtagClick}
              isProjectsLoading={isProjectsLoading}
              isMenuOpen={isBlogMenuOpen}
            />
          }
        />

        <Route
          path="about"
          element={
            <AboutPage
              loggedIn={loggedIn}
              currentUser={currentUser}
              onLogout={handleSignout}
              activePage={activeBlogPage}
              projectsToRender={projectsToRender}
              totalProjects={totalProjects}
              onBlogMenuClick={handleBlogMenuClick}
              onContactClick={onContactClick}
              onAddProjectClick={handleNewProjectPopupOpen}
              onViewAllProjectsClick={viewAllProjectsClick}
              onEditProjectButtonClick={handleEditProjectPopupOpen}
              onDeleteProjectButtonClick={handleDeleteProjectModalOpen}
              projectsNumber={previewProjectsQuantity}
              isMenuOpen={isBlogMenuOpen}
            />
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>

      <MainMenu
        isOpen={isBlogMenuOpen}
        loggedIn={loggedIn}
        onClose={closeBlogMenu}
        onLogout={() => {
          handleSignout();
          closeBlogMenu();
        }}
        theme="blog"
        navigationLabel="Blog navigation"
        menuId="blog-main-menu"
      />

      <NewProjectPopup
        isOpen={isNewProjectPopupOpen}
        onClose={closeAllBlogPopups}
        onAddProject={handleAddProject}
        isSendingReq={isLoading}
      />

      <NewPostPopup
        isOpen={isPostPopupOpen}
        onClose={closeAllBlogPopups}
        isSendingReq={isLoading}
        onAddPost={handleAddPost}
      />

      <EditPostPopup
        isOpen={isEditPostPopupOpen}
        onClose={closeAllBlogPopups}
        isSendingReq={isLoading}
        post={postToEdit}
        onEditPost={handleEditPost}
      />
      <EditProjectPopup
        isOpen={isEditProjectPopupOpen}
        onClose={closeAllBlogPopups}
        isSendingReq={isLoading}
        project={projectToEdit}
        onEditProject={handleEditProject}
      />

      <DeletePostModal
        post={postToDelete}
        isOpen={isDeletePostModalOpen}
        onClose={closeAllBlogPopups}
        onDeletePost={handlePostDelete}
      />

      <DeleteProjectModal
        project={projectToDelete}
        isOpen={isDeleteProjectModalOpen}
        onClose={closeAllBlogPopups}
        onDeleteProject={handleProjectDelete}
      />
    </>
  );
}

export default BlogRoot;
